/**
 * Presentation-only math for the Forecast Score card's monthly bar chart. No
 * scoring/averaging logic lives here — summary.average, band, and the
 * per-period score all come straight from the API; this only turns those
 * numbers into pixel geometry.
 */

export interface BarPoint {
  offset: number
  score: number
  tone?: string | null
}

export interface PlottedBar extends BarPoint {
  heightPx: number
}

export interface MonthlyBarGeometry {
  bars: PlottedBar[]
  /** Pixel offset (from the same baseline as heightPx) of the dashed average line, or null with no average to plot. */
  averageLinePx: number | null
}

/**
 * Bar heights scaled so the tallest bar in the window reaches
 * `maxBarHeightPx`, leaving headroom above it for the per-bar value label —
 * matches the design's own pixel values (e.g. a 98-high month reaching 68px
 * inside an 88px-tall plot area), which a fixed 0-100 domain does not
 * reproduce.
 */
export function monthlyBarGeometry(
  points: BarPoint[],
  average: number | null | undefined,
  maxBarHeightPx = 68,
): MonthlyBarGeometry {
  if (points.length === 0) return { bars: [], averageLinePx: null }

  const maxScore = Math.max(...points.map((p) => p.score))
  const scale = maxScore > 0 ? maxBarHeightPx / maxScore : 0

  const bars = points.map((p) => ({ ...p, heightPx: Math.round(p.score * scale) }))
  const averageLinePx =
    average !== null && average !== undefined ? Math.round(average * scale) : null

  return { bars, averageLinePx }
}

/** Error rate (0-1) presented as the "N% off" figure the design shows next to each component row. */
export function offPercent(error: number | null | undefined): string {
  if (error === null || error === undefined) return '—'
  return `${Math.round(Math.max(0, error) * 100)}%`
}

/**
 * Shared band-tone -> colour mapping, used everywhere a forecast score is
 * rendered (dashboard widget, cashflow table row) so the same band always
 * reads the same colour. The tone values themselves come from the API
 * (config('forecast.score.bands')) — this only maps the enum to a colour.
 */
const FORECAST_TONE_COLOR: Record<string, string> = {
  positive: 'hsl(137,57%,41%)',
  neutral: 'hsl(212,92%,45%)',
  caution: 'hsl(44,66%,50%)',
  negative: 'hsl(356,72%,47%)',
}

export function forecastToneColor(tone?: string | null): string {
  return FORECAST_TONE_COLOR[tone ?? 'neutral'] ?? FORECAST_TONE_COLOR.neutral
}

/**
 * Softer, lighter step of the same tone/hue — the app's existing "-low"
 * palette tier (same family as the "-lowest"/"-low"/"-default"/"-high"
 * tokens used for chips elsewhere), used for the bar chart's own fill so the
 * chart reads as a gentle block of colour rather than a saturated stripe.
 * Value labels stay on the bold forecastToneColor for legibility.
 */
const FORECAST_TONE_COLOR_SOFT: Record<string, string> = {
  positive: 'hsl(134,66%,80%)',
  neutral: 'hsl(204,100%,75%)',
  caution: 'hsl(48,93%,74%)',
  negative: 'hsl(3,100%,90%)',
}

export function forecastToneColorSoft(tone?: string | null): string {
  return FORECAST_TONE_COLOR_SOFT[tone ?? 'neutral'] ?? FORECAST_TONE_COLOR_SOFT.neutral
}
